import { trackEvent } from '@/lib/mixpanel';

// Analytics for the `GET /deals/:dealId/events` SSE stream lifecycle.
//
// Mirrors the WEBSOCKET_* incident tracking in `ws-analytics.ts` so the two
// real-time transports can be compared side by side (connection stability,
// downtime, reconnections). Unlike the WebSocket client — a long-lived global
// with its own event emitter — each SSE stream is owned by a `useDealEventStream`
// effect, so this is a small per-stream state machine created via
// `createDealSseAnalytics(dealId)` rather than a module-level singleton.

type SseDropCause = 'ended' | 'error';

type SseState = 'idle' | 'connected' | 'down';

function now(): number {
  return Date.now();
}

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden === true;
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

export type DealSseAnalytics = {
  /** A stream opened. Emits CONNECTED on first open, RECONNECTED after a drop. */
  onConnected: () => void;
  /** A live stream ended (EOF) or failed mid-read. No-op if not connected. */
  onDropped: (cause: SseDropCause) => void;
};

export function createDealSseAnalytics(
  dealId: string | null
): DealSseAnalytics {
  let state: SseState = 'idle';
  let connectedAt: number | null = null;
  let disconnectedAt: number | null = null;
  let reconnectAttempts = 0;

  return {
    onConnected(): void {
      if (state === 'down') {
        reconnectAttempts += 1;
        trackEvent('DEAL_SSE_RECONNECTED', {
          dealId,
          downtimeMs:
            disconnectedAt !== null ? Math.max(0, now() - disconnectedAt) : 0,
          attempts: reconnectAttempts,
        });
      } else if (state === 'idle') {
        trackEvent('DEAL_SSE_CONNECTED', { dealId });
      }
      state = 'connected';
      connectedAt = now();
      disconnectedAt = null;
    },

    onDropped(cause): void {
      if (state !== 'connected') return;
      trackEvent('DEAL_SSE_DROPPED', {
        dealId,
        cause,
        connectionAgeMs:
          connectedAt !== null ? Math.max(0, now() - connectedAt) : 0,
        wasHidden: isHidden(),
        online: isOnline(),
      });
      state = 'down';
      disconnectedAt = now();
    },
  };
}
